/**
 * PartyForm — controlled form for sender/receiver. Renders name + contact
 * fields plus the service-geo cascading <AddressFields> picker. The legacy
 * free-text city / state / addressLine1 / addressLine2 / postalCode fields
 * on `PartyPayload` are populated from the AddressFields output so the
 * backend continues to receive the existing wire shape. The new geo_*
 * soft-FK fields ride along on the same payload.
 *
 * Designed as a controlled component: the parent owns the value and gets a
 * fully-merged party back on every change. We do not run Zod validation
 * here — the parent step (or the submit step) decides when to validate.
 */
import { useRef, type JSX } from 'react';

import { AddressFields, type AddressValue } from '@upmp/ui';

import { env } from '../../lib/env';
import type { PartyPayload } from '../../lib/api/types';

interface PartyFormProps {
  value: Partial<PartyPayload>;
  onChange: (next: Partial<PartyPayload>) => void;
  idPrefix: string;
}

/** Project the controlled `PartyPayload` onto the `<AddressFields>` value
 *  shape. Lets the picker render the previously-saved selection (e.g. when a
 *  draft is resumed) by hydrating IDs + names from the persisted party. */
function partyToAddressValue(party: Partial<PartyPayload>): AddressValue {
  return {
    countryId: party.geoCountryId ?? null,
    countryName: party.geoCountryName ?? null,
    stateId: party.geoStateId ?? null,
    stateName: party.geoStateName ?? null,
    lgaId: party.geoLgaId ?? null,
    lgaName: party.geoLgaName ?? null,
    wardId: party.geoWardId ?? null,
    wardName: party.geoWardName ?? null,
    addressLine1: party.addressLine1 ?? '',
    addressLine2: party.addressLine2 ?? '',
    postalCode: party.postalCode ?? '',
  };
}

export function PartyForm({ value, onChange, idPrefix }: PartyFormProps): JSX.Element {
  // Keep a ref to the latest value so that event handlers created in one
  // render cycle always merge against the most-recent prop, not a stale
  // closure snapshot. Without this, rapid sequential fills (e.g. Playwright
  // filling name then phone before React re-renders) cause earlier field values
  // to be overwritten by handlers that captured value = {} from the first render.
  const valueRef = useRef(value);
  valueRef.current = value;

  const update = (patch: Partial<PartyPayload>): void => {
    onChange({ ...valueRef.current, ...patch });
  };

  const addressValue = partyToAddressValue(value);

  const handleAddressChange = (next: AddressValue): void => {
    // Mirror the geo selection onto the legacy free-text fields so the wire
    // shape stays identical and downstream code that reads .state / .city
    // keeps working. State + city fall back to the geo names; LGA wins for
    // city when present, otherwise we use the state name as a coarse city.
    const stateText = next.stateName ?? '';
    const cityText = next.lgaName ?? next.stateName ?? '';
    update({
      addressLine1: next.addressLine1,
      addressLine2: next.addressLine2 || undefined,
      postalCode: next.postalCode || undefined,
      city: cityText,
      state: stateText,
      // Default the legacy ISO-2 country code from the geo selection when we
      // can recognise it. Nigeria is the universe of this slice; other
      // countries fall back to the existing value (or the 'NG' default).
      countryCode:
        next.countryName?.toUpperCase() === 'NIGERIA'
          ? 'NG'
          : (valueRef.current.countryCode ?? 'NG'),
      geoCountryId: next.countryId ?? undefined,
      geoCountryName: next.countryName ?? undefined,
      geoStateId: next.stateId ?? undefined,
      geoStateName: next.stateName ?? undefined,
      geoLgaId: next.lgaId ?? undefined,
      geoLgaName: next.lgaName ?? undefined,
      geoWardId: next.wardId ?? undefined,
      geoWardName: next.wardName ?? undefined,
    });
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <Field id={`${idPrefix}-name`} label="Full name *">
          <input
            id={`${idPrefix}-name`}
            type="text"
            value={value.name ?? ''}
            onChange={(e) => update({ name: e.target.value })}
            className="mt-1 w-full rounded border border-slate-300 px-3 py-2 text-sm"
          />
        </Field>
        <Field id={`${idPrefix}-phone`} label="Phone *">
          <input
            id={`${idPrefix}-phone`}
            type="tel"
            value={value.phone ?? ''}
            onChange={(e) => update({ phone: e.target.value })}
            className="mt-1 w-full rounded border border-slate-300 px-3 py-2 text-sm"
          />
        </Field>
        <Field id={`${idPrefix}-email`} label="Email" wide>
          <input
            id={`${idPrefix}-email`}
            type="email"
            value={value.email ?? ''}
            onChange={(e) => update({ email: e.target.value })}
            className="mt-1 w-full rounded border border-slate-300 px-3 py-2 text-sm"
          />
        </Field>
      </div>

      <AddressFields
        geoBaseUrl={env.serviceGeoBaseUrl}
        value={addressValue}
        onChange={handleAddressChange}
      />
    </div>
  );
}

interface FieldProps {
  id: string;
  label: string;
  wide?: boolean;
  children: JSX.Element;
}

function Field({ id, label, wide, children }: FieldProps): JSX.Element {
  return (
    <div className={wide === true ? 'md:col-span-2' : ''}>
      <label htmlFor={id} className="text-xs font-medium text-slate-700">
        {label}
      </label>
      {children}
    </div>
  );
}
